import { Lock } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../../lib/utils';
import {
  StatePlaceholder,
  type StatePlaceholderIconProps,
  type StatePlaceholderProps,
} from '../state-placeholder';

import { restrictedPlaceholderTestId } from './restricted-placeholder-test-ids';

/**
 * `role` is absent: reinstating `alert` semantics is precisely what this component exists to
 * prevent, so it must not be reachable from a call site. `testId` is absent because the
 * namespace is what makes `restrictedPlaceholderTestIds` stable.
 */
export type RestrictedPlaceholderProps = Omit<
  StatePlaceholderProps,
  'role' | 'testId'
>;

/**
 * Defaults to a lock. Pass children to replace the glyph — `<RestrictedPlaceholder.Icon>`
 * owns the sizing, the tile and `aria-hidden`, so any node is safe here.
 */
const RestrictedIcon = ({
  children,
  className,
  tileClassName,
}: StatePlaceholderIconProps) => (
  <StatePlaceholder.Icon
    // `text-gray-*` is theme-flipped through `--themed-gray-*`, so the class does NOT name one
    // colour: `gray-500` is #6b7280 in light and #9ca3af in dark — the same value
    // `--muted-foreground` carries in dark. Both modes were measured against the surfaces this
    // renders on: 4.83:1 on card and 4.63:1 on the tile in light, 5.90:1 and 8.02:1 in dark.
    // What `--muted-foreground` fails is LIGHT mode specifically, at 2.51:1, under the 3:1
    // WCAG 1.4.11 floor. At `inline` this glyph is the only non-text carrier of "blocked".
    className={cn('text-gray-500', className)}
    tileClassName={cn('bg-empty-state-bg', tileClassName)}
  >
    {children ?? <Lock />}
  </StatePlaceholder.Icon>
);

/**
 * The shared "you do not have permission" state.
 *
 * ## Why this is not an error component
 *
 * A denial is an ordinary, authoritative outcome — the reader's role simply does not include
 * this record. Rendering it in `destructive` red with a "Try again" control, as the consent
 * log did before this, makes two false claims at once: that the system failed, and that
 * pressing the button could change the answer. Neither is true, so this component is drawn in
 * neutral tones, carries no retry, and carries `role="status"` rather than `role="alert"`.
 *
 * `role="status"` here is a claim about SEMANTICS, not a promise of an announcement. A live
 * region is only announced reliably when it is in the DOM before its text changes, and this
 * root mounts together with its own text — a call site that swaps a loading state for this
 * component creates the region and fills it in the same commit, which NVDA, JAWS and
 * VoiceOver handle differently. Guaranteeing the announcement is the call site's job: it
 * needs a region that outlives the state swap, which a presentational placeholder cannot own
 * without nesting one live region inside another. What this component does guarantee is that
 * a denial is never escalated to `alert`.
 *
 * Restricted resolves BEFORE error in a data-state branch, exactly as error already resolves
 * before empty (`ConsentHistoryLog`): each state asserts a different fact about the record and
 * none may stand in for another.
 *
 * ## It must not be a dead end
 *
 * Refusing the retry is not the same as refusing every exit. The description names who can
 * lift the restriction — a link is fine, and often better — and `Action` carries navigation
 * away from it. A clinician who cannot see a record still needs to know what to do next.
 * `Action` is NEVER a retry.
 *
 * ## Reach for `<Permission>` first
 *
 * Where the whole affordance can be hidden, hide it. This component is for the places where a
 * silent gap would be unexplained — a panel the reader navigated to on purpose, a popover they
 * just opened, a route they followed a link into.
 *
 * Presentation-only, like its `not-found` and `coming-soon` siblings: callers pass localized
 * strings, because `libs/ui` carries no i18n.
 *
 * @example
 * <RestrictedPlaceholder density="inline">
 *   <RestrictedPlaceholder.Icon />
 *   <RestrictedPlaceholder.Description>
 *     {t('smsConsent.history.states.restricted')}
 *   </RestrictedPlaceholder.Description>
 * </RestrictedPlaceholder>
 */
const RestrictedPlaceholderRoot = React.forwardRef<
  HTMLDivElement,
  RestrictedPlaceholderProps
>(({ className, children, ...rest }, ref) => (
  <StatePlaceholder
    ref={ref}
    {...rest}
    role="status"
    testId={restrictedPlaceholderTestId}
    className={cn('w-full', className)}
  >
    {children}
  </StatePlaceholder>
));

RestrictedPlaceholderRoot.displayName = 'RestrictedPlaceholder';
RestrictedIcon.displayName = 'RestrictedPlaceholder.Icon';

export const RestrictedPlaceholder = Object.assign(RestrictedPlaceholderRoot, {
  Icon: RestrictedIcon,
  Content: StatePlaceholder.Content,
  Title: StatePlaceholder.Title,
  Description: StatePlaceholder.Description,
  Action: StatePlaceholder.Action,
});
